import { CircleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  presentApiError,
  type ApiErrorFieldRule,
  type ApiErrorPolicyOptions,
} from "@/lib/api-error-policy";

export interface FormValidationIssue {
  id: string;
  message: string;
  kind: "field" | "form" | "server";
  controlId?: string;
  requestId?: string | null;
}

export function apiErrorFormIssue(
  error: unknown,
  options: Omit<ApiErrorPolicyOptions, "fieldRules"> & { id: string },
): FormValidationIssue {
  const { id, ...policyOptions } = options;
  const presentation = presentApiError(error, policyOptions);
  return {
    id,
    message: presentation.kind === "domain" ? presentation.message : policyOptions.fallbackMessage,
    kind: "server",
    requestId: presentation.requestId,
  };
}

export interface ApiErrorFormFieldIssue<Field extends string> {
  field: Field;
  issue: FormValidationIssue;
}

export type ApiErrorFormPresentation<Field extends string> =
  | { kind: "field"; fields: ApiErrorFormFieldIssue<Field>[] }
  | { kind: "server"; issue: FormValidationIssue };

export function presentApiErrorForm<Field extends string>(
  error: unknown,
  options: Omit<ApiErrorPolicyOptions, "fieldRules"> & {
    id: string;
    fieldRules: readonly (Omit<ApiErrorFieldRule, "field"> & { field: Field })[];
    fieldControls: Readonly<Record<Field, string>>;
  },
): ApiErrorFormPresentation<Field> {
  const { id, fieldControls, ...policyOptions } = options;
  const presentation = presentApiError(error, policyOptions);
  if (presentation.kind === "field") {
    const fields = presentation.issues.map(({ field, message }) => {
      const typedField = field as Field;
      const controlId = fieldControls[typedField];
      return {
        field: typedField,
        issue: {
          id: `${controlId}-error`,
          message,
          kind: "field" as const,
          controlId,
          requestId: presentation.requestId,
        },
      };
    });
    return { kind: "field", fields };
  }
  return {
    kind: "server",
    issue: {
      id,
      message:
        presentation.kind === "domain" ? presentation.message : policyOptions.fallbackMessage,
      kind: "server",
      requestId: presentation.requestId,
    },
  };
}

export function focusValidationControl(controlId: string): void {
  const control = document.getElementById(controlId);
  if (!(control instanceof HTMLElement)) return;
  control.focus({ preventScroll: true });
  const rect = control.getBoundingClientRect();
  if (rect.top < 0 || rect.bottom > window.innerHeight) {
    control.scrollIntoView({ block: "center", behavior: "smooth" });
  }
}

export function FormFieldError({
  issue,
}: {
  issue?: FormValidationIssue;
}): React.ReactElement | null {
  const { t } = useTranslation();
  if (!issue) return null;
  return (
    <div
      id={issue.id}
      data-testid={issue.id}
      role={issue.kind === "server" ? "alert" : undefined}
      className="flex items-start gap-1 text-xs text-[var(--alert-red)]"
    >
      <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span className="flex min-w-0 flex-col gap-1">
        <span>{issue.message}</span>
        {issue.requestId ? (
          <span className="text-muted-foreground font-mono text-xs">
            {t("common.apiError.requestId", { id: issue.requestId })}
          </span>
        ) : null}
      </span>
    </div>
  );
}

interface FormErrorSummaryProps {
  issues: FormValidationIssue[];
  countLabel: (count: number) => string;
  "data-testid"?: string;
}

export function FormErrorSummary({
  issues,
  countLabel,
  "data-testid": testId = "form-error-summary",
}: FormErrorSummaryProps): React.ReactElement {
  const { t } = useTranslation();
  if (issues.length === 0) {
    return <div aria-live="polite" data-testid={testId} />;
  }
  const fieldIssues = issues.filter((issue) => issue.kind === "field");
  const blockIssues = issues.filter((issue) => issue.kind !== "field");
  const showFieldSummary = fieldIssues.length > 1;
  if (!showFieldSummary && blockIssues.length === 0) {
    return <div aria-live="polite" data-testid={testId} />;
  }
  const hasServerIssue = blockIssues.some((issue) => issue.kind === "server");
  return (
    <div aria-live="polite" data-testid={testId}>
      <div
        role={hasServerIssue ? "alert" : undefined}
        data-testid={`${testId}-content`}
        className="tone-danger flex w-full items-start gap-2 rounded-[var(--radius-card)] px-3 py-2 text-sm"
      >
        <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1">
          {showFieldSummary && <div className="font-medium">{countLabel(fieldIssues.length)}</div>}
          {blockIssues.map((issue) => (
            <div key={issue.id}>
              <div>{issue.message}</div>
              {issue.requestId ? (
                <div className="mt-1 font-mono text-xs" data-testid={`${issue.id}-request-id`}>
                  {t("common.apiError.requestId", { id: issue.requestId })}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
